/**
 * Monitoring Page
 * @description Tenant 관리자 - 모니터링 페이지 (실제 API 연동)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './tenant-admin.css';
import {
  Badge, Button, Card, CardContent, CardHeader, CardTitle, Progress, Select,
  SelectContent, SelectItem, SelectTrigger, SelectValue, Tabs, TabsContent,
  TabsList, TabsTrigger
} from '@shared/ui/primitives';
import {
    Activity,
    BarChart3,
    AlertTriangle,
    CheckCircle,
    Clock,
    TrendingUp,
    Server,
    RefreshCw,
    Settings,
    Download,
    Filter,
    Eye,
    HardDrive,
    Cpu,
    MemoryStick,
    Network,
    Bell,
    Calendar,
    ArrowUp,
    ArrowDown,
    Minus
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
    getMonitoringMetrics,
    getMonitoringServices,
    getMonitoringAlerts,
    getMonitoringSummary,
    getMonitoringPerformance,
    resolveMonitoringAlert,
    type MonitoringMetrics,
    type MonitoringAlert,
    type MonitoringPerformancePoint,
} from '@/services/tenantConfigApi';

const THRESHOLD = { cpu: { warning: 70, critical: 90 }, memory: { warning: 70, critical: 90 }, disk: { warning: 80, critical: 95 } };
const LOADING_SAFETY_TIMEOUT_MS = 22000; // 로딩 무한 대기 방지

function getMetricStatus(value: number, warning: number, critical: number): 'normal' | 'warning' | 'error' {
    if (value >= critical) return 'error';
    if (value >= warning) return 'warning';
    return 'normal';
}

function formatThroughput(bytesPerSec: number): { value: number; unit: 'B/s' | 'KB/s' | 'MB/s' } {
    const bps = Number.isFinite(bytesPerSec) ? Math.max(0, bytesPerSec) : 0;
    if (bps >= 1024 * 1024) return { value: bps / (1024 * 1024), unit: 'MB/s' };
    if (bps >= 1024) return { value: bps / 1024, unit: 'KB/s' };
    return { value: bps, unit: 'B/s' };
}

export const MonitoringPage: React.FC = () => {
    const { user } = useAuthStore();
    const tenantId = user?.userType === 'tenant-user' ? user.tenantId : '';

    const [refreshing, setRefreshing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState('1h');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [metrics, setMetrics] = useState<MonitoringMetrics | null>(null);
    const [servicesRes, setServicesRes] = useState<{ services: Array<{ name: string; status: string }>; total: number; ok: number } | null>(null);
    const [alerts, setAlerts] = useState<MonitoringAlert[]>([]);
    const [summary, setSummary] = useState<{
        activeServices: number;
        totalServices: number;
        uptimePercent: number;
        unresolvedAlerts: number;
    } | null>(null);
    const [performancePoints, setPerformancePoints] = useState<MonitoringPerformancePoint[]>([]);

    const loadData = useCallback(async () => {
        if (!tenantId) return;
        try {
            setError(null);
            const [metricsData, servicesData, alertsData, summaryData, performanceData] = await Promise.all([
                getMonitoringMetrics(tenantId),
                getMonitoringServices(tenantId),
                getMonitoringAlerts(tenantId, { timeRange, resolved: undefined }),
                getMonitoringSummary(tenantId),
                getMonitoringPerformance(tenantId, { limit: 24 }),
            ]);
            setMetrics(metricsData);
            setServicesRes(servicesData);
            setAlerts(alertsData.alerts ?? []);
            setSummary(summaryData);
            setPerformancePoints(performanceData.points ?? []);
        } catch (e) {
            setError((e as Error)?.message ?? '모니터링 데이터를 불러오지 못했습니다.');
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, [tenantId, timeRange]);

    const loadingSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!tenantId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        if (loadingSafetyTimeoutRef.current) clearTimeout(loadingSafetyTimeoutRef.current);
        loadingSafetyTimeoutRef.current = setTimeout(() => {
            setLoading((prev) => {
                if (prev) {
                    setError('모니터링 응답이 지연되고 있습니다. 백엔드(3100 포트) 및 네트워크를 확인해 주세요.');
                    return false;
                }
                return prev;
            });
        }, LOADING_SAFETY_TIMEOUT_MS);
        loadData();
        return () => {
            if (loadingSafetyTimeoutRef.current) {
                clearTimeout(loadingSafetyTimeoutRef.current);
                loadingSafetyTimeoutRef.current = null;
            }
        };
    }, [tenantId, loadData]);

    useEffect(() => {
        if (!autoRefresh || !tenantId) return;
        const interval = setInterval(loadData, 30000);
        return () => clearInterval(interval);
    }, [autoRefresh, tenantId, loadData]);

    const handleRefresh = async () => {
        if (!tenantId) return;
        setRefreshing(true);
        await loadData();
    };

    const handleResolveAlert = async (alertId: string) => {
        if (!tenantId) return;
        try {
            await resolveMonitoringAlert(tenantId, alertId);
            await loadData();
        } catch (e) {
            alert((e as Error)?.message ?? '알림 해제에 실패했습니다.');
        }
    };

    // API 메트릭 → 카드용 배열 (CPU, 메모리, 디스크 첫 번째, 네트워크)
    const networkTotalBytesSec = (metrics?.network?.rxBytesSec ?? 0) + (metrics?.network?.txBytesSec ?? 0);
    const networkDisplay = formatThroughput(networkTotalBytesSec);

    const systemMetrics = metrics
        ? [
            {
                name: 'CPU 사용률',
                value: Math.round((metrics.cpu?.usagePercent ?? 0) * 10) / 10,
                unit: '%',
                status: getMetricStatus(metrics.cpu?.usagePercent ?? 0, THRESHOLD.cpu.warning, THRESHOLD.cpu.critical),
                icon: Cpu,
                color: 'blue',
                threshold: THRESHOLD.cpu,
            },
            {
                name: '메모리 사용률',
                value: Math.round((metrics.memory?.usedPercent ?? 0) * 10) / 10,
                unit: '%',
                status: getMetricStatus(metrics.memory?.usedPercent ?? 0, THRESHOLD.memory.warning, THRESHOLD.memory.critical),
                icon: MemoryStick,
                color: 'yellow',
                threshold: THRESHOLD.memory,
            },
            {
                name: '디스크 사용률',
                value: metrics.disk?.[0] ? Math.round(metrics.disk[0].usedPercent * 10) / 10 : 0,
                unit: '%',
                status: metrics.disk?.[0]
                    ? getMetricStatus(metrics.disk[0].usedPercent, THRESHOLD.disk.warning, THRESHOLD.disk.critical)
                    : 'normal',
                icon: HardDrive,
                color: 'green',
                threshold: THRESHOLD.disk,
            },
            {
                name: '네트워크 I/O',
                value: networkDisplay.value,
                unit: networkDisplay.unit,
                status: 'normal' as const,
                icon: Network,
                color: 'purple',
                threshold: { warning: 5, critical: 8 },
            },
        ]
        : [];

    const serviceStatus = (servicesRes?.services ?? [])
        .filter((s) => (s.name ?? '').toLowerCase() !== 'template')
        .map((s) => ({
            name: s.name,
            status: s.status,
            health: s.status === 'running' ? 100 : s.status === 'warning' ? 50 : 0,
            icon: Server,
        }));

    const recentAlerts = alerts;
    const perfSeries = performancePoints.slice(-12);
    const latestPerf = perfSeries[perfSeries.length - 1];

    if (!tenantId) {
        return (
            <div className="tenant-admin-page flex items-center justify-center min-h-[300px]">
                <p className="text-muted-foreground">테넌트 사용자로 로그인해 주세요.</p>
            </div>
        );
    }

    if (loading && !metrics) {
        return (
            <div className="tenant-admin-page flex items-center justify-center min-h-[300px]">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'running':
            case 'normal':
                return <Badge className="bg-green-100 text-green-800 border-green-200">정상</Badge>;
            case 'warning':
                return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">주의</Badge>;
            case 'error':
            case 'stopped':
                return <Badge variant="destructive">오류</Badge>;
            default:
                return <Badge variant="secondary">알 수 없음</Badge>;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'running':
            case 'normal':
                return <CheckCircle className="w-5 h-5 text-green-600" />;
            case 'warning':
                return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
            case 'error':
            case 'stopped':
                return <AlertTriangle className="w-5 h-5 text-red-600" />;
            default:
                return <AlertTriangle className="w-5 h-5 text-gray-600" />;
        }
    };

    const getAlertIcon = (type: string) => {
        switch (type) {
            case 'error':
                return <AlertTriangle className="w-4 h-4 text-red-600" />;
            case 'warning':
                return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
            case 'info':
                return <CheckCircle className="w-4 h-4 text-blue-600" />;
            default:
                return <AlertTriangle className="w-4 h-4 text-gray-600" />;
        }
    };

    const getSeverityBadge = (severity: string) => {
        switch (severity) {
            case 'high':
                return <Badge variant="destructive" className="text-xs">높음</Badge>;
            case 'medium':
                return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 text-xs">보통</Badge>;
            case 'low':
                return <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">낮음</Badge>;
            default:
                return <Badge variant="secondary" className="text-xs">알 수 없음</Badge>;
        }
    };

    const getTrendIcon = (trend: string, change: string) => {
        switch (trend) {
            case 'up':
                return (
                    <div className="flex items-center gap-1 text-red-600">
                        <ArrowUp className="w-3 h-3" />
                        <span className="text-xs">{change}</span>
                    </div>
                );
            case 'down':
                return (
                    <div className="flex items-center gap-1 text-green-600">
                        <ArrowDown className="w-3 h-3" />
                        <span className="text-xs">{change}</span>
                    </div>
                );
            case 'stable':
                return (
                    <div className="flex items-center gap-1 text-gray-600">
                        <Minus className="w-3 h-3" />
                        <span className="text-xs">{change}</span>
                    </div>
                );
            default:
                return null;
        }
    };

    const getMetricColor = (status: string) => {
        switch (status) {
            case 'normal':
                return 'bg-green-50 border-green-200';
            case 'warning':
                return 'bg-yellow-50 border-yellow-200';
            case 'error':
                return 'bg-red-50 border-red-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    return (
        <div className="tenant-admin-page space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">모니터링</h1>
                    <p className="text-muted-foreground mt-1">
                        시스템 상태 및 성능을 실시간으로 모니터링합니다.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="w-32">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="5m">5분</SelectItem>
                            <SelectItem value="15m">15분</SelectItem>
                            <SelectItem value="1h">1시간</SelectItem>
                            <SelectItem value="6h">6시간</SelectItem>
                            <SelectItem value="24h">24시간</SelectItem>
                            <SelectItem value="7d">7일</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={autoRefresh ? 'bg-green-50 border-green-200' : ''}
                    >
                        <Activity className={`w-4 h-4 mr-2 ${autoRefresh ? 'text-green-600' : ''}`} />
                        자동 새로고침
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        새로고침
                    </Button>
                </div>
            </div>

            {/* System Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {systemMetrics.map((metric, index) => {
                    const IconComponent = metric.icon;
                    return (
                        <Card key={index} className={`transition-all duration-200 hover:shadow-md ${getMetricColor(metric.status)}`}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className={`p-2 rounded-lg bg-${metric.color}-100`}>
                                        <IconComponent className={`w-5 h-5 text-${metric.color}-600`} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-sm font-medium text-muted-foreground">{metric.name}</p>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-2xl font-bold text-foreground">
                                            {typeof metric.value === 'number' ? metric.value.toFixed(metric.unit === '%' ? 1 : 2) : metric.value}
                                        </span>
                                        <span className="text-sm text-muted-foreground">{metric.unit}</span>
                                    </div>
                                    {typeof metric.value === 'number' && metric.unit === '%' && 'threshold' in metric && (
                                        <div className="space-y-1">
                                            <Progress
                                                value={metric.value}
                                                className="h-2"
                                                indicatorClassName={
                                                    metric.value >= (metric.threshold as { critical: number }).critical ? 'bg-red-500' :
                                                        metric.value >= (metric.threshold as { warning: number }).warning ? 'bg-yellow-500' :
                                                            'bg-green-500'
                                                }
                                            />
                                            <div className="flex justify-between text-xs text-muted-foreground">
                                                <span>0%</span>
                                                <span>100%</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 text-sm">
                    {error}
                </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-500 rounded-lg">
                                <Server className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-sm text-blue-700">활성 서비스</p>
                                <p className="text-2xl font-bold text-blue-900">
                                    {summary ? `${summary.activeServices}/${summary.totalServices}` : `${serviceStatus.filter(s => s.status === 'running').length}/${serviceStatus.length}`}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-yellow-500 rounded-lg">
                                <Bell className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-sm text-yellow-700">미해결 알림</p>
                                <p className="text-2xl font-bold text-yellow-900">
                                    {summary?.unresolvedAlerts ?? recentAlerts.filter(a => !a.resolved).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-500 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-sm text-green-700">평균 가동률</p>
                                <p className="text-2xl font-bold text-green-900">{summary?.uptimePercent ?? 0}%</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content */}
            <Tabs defaultValue="services" className="space-y-6">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="services" className="flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        서비스 상태
                    </TabsTrigger>
                    <TabsTrigger value="alerts" className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        알림 내역
                        {recentAlerts.filter(a => !a.resolved).length > 0 && (
                            <Badge className="ml-1 bg-red-100 text-red-800 text-xs px-1.5 py-0.5">
                                {recentAlerts.filter(a => !a.resolved).length}
                            </Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="performance" className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        성능 분석
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="services" className="space-y-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                            <CardTitle className="text-xl font-semibold">서비스 상태</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm">
                                    <Download className="w-4 h-4 mr-2" />
                                    보고서 다운로드
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                                    새로고침
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {serviceStatus.map((service, index) => {
                                    const IconComponent = service.icon;
                                    return (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="flex items-center gap-3">
                                                    {getStatusIcon(service.status)}
                                                    <div className="p-2 bg-gray-100 rounded-lg">
                                                        <IconComponent className="w-5 h-5 text-gray-600" />
                                                    </div>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="font-medium text-lg">{service.name}</h3>
                                                        {getStatusBadge(service.status)}
                                                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                            <span>상태:</span>
                                                            <span className="font-medium">{service.health}%</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                                                        <RefreshCw className="w-3 h-3" />
                                                        <span>마지막 확인: {(servicesRes as any)?.timestamp ? new Date((servicesRes as any).timestamp).toLocaleString('ko-KR') : '-'}</span>
                                                    </div>
                                                    <div className="mt-2">
                                                        <Progress value={service.health} className="h-2" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 ml-4">
                                                <Button variant="outline" size="sm">
                                                    <Eye className="w-4 h-4 mr-1" />
                                                    로그
                                                </Button>
                                                <Button variant="outline" size="sm">
                                                    <Settings className="w-4 h-4 mr-1" />
                                                    설정
                                                </Button>
                                                {service.status === 'warning' && (
                                                    <Button variant="outline" size="sm" className="text-orange-600 border-orange-200">
                                                        <RefreshCw className="w-4 h-4 mr-1" />
                                                        재시작
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="alerts" className="space-y-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                            <CardTitle className="text-xl font-semibold">알림 내역</CardTitle>
                            <div className="flex items-center gap-2">
                                <Select defaultValue="all">
                                    <SelectTrigger className="w-32">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">전체</SelectItem>
                                        <SelectItem value="unresolved">미해결</SelectItem>
                                        <SelectItem value="resolved">해결됨</SelectItem>
                                        <SelectItem value="high">높음</SelectItem>
                                        <SelectItem value="medium">보통</SelectItem>
                                        <SelectItem value="low">낮음</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button variant="outline" size="sm">
                                    <Filter className="w-4 h-4 mr-2" />
                                    필터
                                </Button>
                                <Button variant="outline" size="sm">
                                    모든 알림 보기
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {recentAlerts.map((alert) => (
                                    <div
                                        key={alert.id}
                                        className={`flex items-start gap-4 p-4 border rounded-lg transition-all hover:shadow-sm ${alert.resolved ? 'opacity-60 bg-gray-50' : 'bg-white'
                                            }`}
                                    >
                                        <div className="flex-shrink-0 mt-1">
                                            {getAlertIcon(alert.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-4 mb-2">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h4 className="font-medium text-foreground">{alert.message}</h4>
                                                    {getSeverityBadge(alert.severity)}
                                                    {alert.resolved && (
                                                        <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                                                            해결됨
                                                        </Badge>
                                                    )}
                                                </div>
                                                {!alert.resolved && (
                                                    <Button variant="outline" size="sm" className="flex-shrink-0" onClick={() => handleResolveAlert(alert.id)}>
                                                        해결
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    <span>{alert.timestamp ? new Date(alert.timestamp).toLocaleString('ko-KR') : '-'}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Server className="w-3 h-3" />
                                                    <span>{alert.service ?? '-'}</span>
                                                </div>
                                                {alert.duration && (
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        <span>지속시간: {alert.duration}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="performance" className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-xl font-semibold">CPU/메모리 추이</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {perfSeries.length === 0 ? (
                                    <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                                        스냅샷 이력이 없어 성능 추이를 표시할 수 없습니다.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="text-sm text-muted-foreground">
                                            현재 CPU {latestPerf?.cpuPercent?.toFixed(1)}% / 메모리 {latestPerf?.memoryPercent?.toFixed(1)}%
                                        </div>
                                        {perfSeries.map((p) => (
                                            <div key={`cm-${p.timestamp}`} className="space-y-1">
                                                <div className="flex justify-between text-xs text-muted-foreground">
                                                    <span>{new Date(p.timestamp).toLocaleTimeString('ko-KR')}</span>
                                                    <span>CPU {p.cpuPercent.toFixed(1)}% / MEM {p.memoryPercent.toFixed(1)}%</span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <Progress value={p.cpuPercent} className="h-2" />
                                                    <Progress value={p.memoryPercent} className="h-2" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-xl font-semibold">네트워크 처리량 추이</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {perfSeries.length === 0 ? (
                                    <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                                        스냅샷 이력이 없어 처리량 추이를 표시할 수 없습니다.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="text-sm text-muted-foreground">
                                            {(() => {
                                                const d = formatThroughput((latestPerf?.networkMbSec ?? 0) * 1024 * 1024);
                                                return `현재 처리량 ${d.value.toFixed(d.unit === 'MB/s' ? 3 : d.unit === 'KB/s' ? 2 : 0)} ${d.unit}`;
                                            })()}
                                        </div>
                                        {perfSeries.map((p) => {
                                            const value = Math.min(100, p.networkMbSec * 20);
                                            const d = formatThroughput((p.networkMbSec ?? 0) * 1024 * 1024);
                                            return (
                                                <div key={`nw-${p.timestamp}`} className="space-y-1">
                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                        <span>{new Date(p.timestamp).toLocaleTimeString('ko-KR')}</span>
                                                        <span>{d.value.toFixed(d.unit === 'MB/s' ? 3 : d.unit === 'KB/s' ? 2 : 0)} {d.unit}</span>
                                                    </div>
                                                    <Progress value={value} className="h-2" />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-xl font-semibold">오류율/장애 추이</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {perfSeries.length === 0 ? (
                                    <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                                        스냅샷 이력이 없어 오류율 추이를 표시할 수 없습니다.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="text-sm text-muted-foreground">
                                            현재 오류율 {latestPerf?.errorRatePercent?.toFixed(1)}% / 미해결 알림 {latestPerf?.unresolvedAlerts ?? 0}건
                                        </div>
                                        {perfSeries.map((p) => (
                                            <div key={`er-${p.timestamp}`} className="space-y-1">
                                                <div className="flex justify-between text-xs text-muted-foreground">
                                                    <span>{new Date(p.timestamp).toLocaleTimeString('ko-KR')}</span>
                                                    <span>오류율 {p.errorRatePercent.toFixed(1)}% / 장애 {p.downServices} / 알림 {p.alertCount}</span>
                                                </div>
                                                <Progress value={p.errorRatePercent} className="h-2" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-xl font-semibold">리소스 사용률</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {systemMetrics.slice(0, 3).map((metric, index) => {
                                        const IconComponent = metric.icon;
                                        return (
                                            <div key={index} className="flex items-center gap-3">
                                                <IconComponent className="w-5 h-5 text-gray-600" />
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-center mb-1">
                                                        <span className="text-sm font-medium">{metric.name}</span>
                                                        <span className="text-sm text-muted-foreground">
                                                            {metric.value}{metric.unit}
                                                        </span>
                                                    </div>
                                                    <Progress
                                                        value={typeof metric.value === 'number' ? metric.value : 0}
                                                        className="h-2"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

MonitoringPage.displayName = 'MonitoringPage';